import { FC, useRef } from "react";
import { Button, Skeleton, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { DownloadIcon } from "../../../../../assets/icons";
import SimpleBarChart from "../../../../../components/charts/SimpleBarChart";
import { DataPoint } from "../../../../../components/charts/types";
import PaperCard from "../../../../../components/widgets/PaperCard";
import PaperCardHeader from "../../../../../components/widgets/PaperCard/PaperCardHeader";
import { useFiltersRegistry } from "../../../../../context/FiltersProvider";
import {
	EmissionsScopeResultsDTO,
	ExtendedMeasurableValueDTO,
	ResultsBasePayload,
} from "../../../../../datasources/api-client/data-contracts";
import { ApiRoutes } from "../../../../../datasources/networking/apiRoutes";
import { useNavigationPreferences } from "../../../../../hooks/usePreferences";
import { useReadResource } from "../../../../../hooks/useResource";
import { useChartColors } from "../../../../../theme/theme";

interface Props {
	chartMode: "tCO2e" | "% tCO2e";
	onChartModeChange: (v: "tCO2e" | "% tCO2e") => void;
}

const ResultsScopeEmissions: FC<Props> = ({ chartMode, onChartModeChange }) => {
	const { chartColors } = useChartColors();
	const containerRef = useRef<HTMLDivElement | null>(null);

	const { t } = useTranslation("feature.results");

	const { appliedFilters } = useFiltersRegistry();
	const { navigationPreferences } = useNavigationPreferences();

	const {
		data: response,
		reload,
		isLoading,
	} = useReadResource<ResultsBasePayload, EmissionsScopeResultsDTO>(
		{ route: ApiRoutes.ResultsScopeEmissions, query: { perspective: navigationPreferences.transportsPerspective } },
		{
			readMethod: "post",
		},
		{ ...appliedFilters.payload }
	);

	const chartData: DataPoint[] = Object.values(response?.data ?? {}).map(
		(value: ExtendedMeasurableValueDTO, index) => {
			const lablePosition = index + 1; // Scopes are 1, 2, 3

			return {
				label: t(`emissions/scope/${lablePosition}`),
				value: value.value,
				measure: value.measure,
				percentage: value.percentage,
				color: chartColors[`scope${lablePosition}Bar` as keyof typeof chartColors],
			};
		}
	);

	const buttonActions = (
		<Stack pt={3} justifyContent={"center"} spacing={2} direction="row">
			<Stack direction="row" spacing={1}>
				<Button disabled onClick={() => {}} size="medium" color="primary" startIcon={<DownloadIcon />}>
					<Typography variant="body2" color="primary">
						{t("ghg report")}
					</Typography>
				</Button>
				<Button disabled onClick={() => {}} size="medium" color="primary" startIcon={<DownloadIcon />}>
					<Typography variant="body2" color="primary">
						{t("iso report")}
					</Typography>
				</Button>
				<Button disabled onClick={() => {}} size="medium" color="primary" startIcon={<DownloadIcon />}>
					<Typography variant="body2" color="primary">
						{t("gri report")}
					</Typography>
				</Button>
			</Stack>
		</Stack>
	);

	if (isLoading) return <Skeleton variant="rounded" height="100%" width="100%" />;

	return (
		<PaperCard
			ref={containerRef}
			header={
				<PaperCardHeader containerRef={containerRef} title={t("emissions/scope/title")} onReload={reload} />
			}
			content={
				<Stack direction="column" height={"100%"} width={"100%"} justifyContent={"flex-end"}>
					<SimpleBarChart
						data={chartData}
						chartName={"scope-emissions"}
						onSeriesClicked={(index) => alert(index)}
						chartMode={chartMode}
						onChartModeChange={onChartModeChange}
					/>
					{buttonActions}
				</Stack>
			}
		/>
	);
};

export default ResultsScopeEmissions;
